import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, X } from "lucide-react";
import { format } from 'date-fns';

const typeLabels = {
    main_print: 'Основной тираж',
    reprint: 'Допечатка'
};

export default function EditPrintRunModal({ isOpen, onClose, onSave, printRun, allBooks, allWarehouses }) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        book_id: '',
        type: '',
        quantity: '',
        status: '',
        expected_delivery_date: '',
        document_date: '',
        notes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (printRun && isOpen) {
            // Create a deep copy to avoid modifying the original state directly
            setFormData({
                ...printRun,
                expected_delivery_date: printRun.expected_delivery_date ? 
                    format(new Date(printRun.expected_delivery_date), 'yyyy-MM-dd') : '',
                document_date: printRun.document_date ? 
                    format(new Date(printRun.document_date), 'yyyy-MM-dd') : ''
            });
        }
    }, [printRun, isOpen]);

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Валидация формы
        if (!formData.book_id) {
            alert('Пожалуйста, выберите книгу');
            return;
        }
        
        if (!formData.warehouse_id) {
            alert('Пожалуйста, выберите склад назначения');
            return;
        }
        
        if (!formData.quantity || formData.quantity <= 0) {
            alert('Пожалуйста, укажите корректное количество');
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            await onSave(formData);
        } catch (error) {
            console.error("Error saving print run:", error);
        }
        
        setIsSubmitting(false);
    };

    if (!isOpen || !printRun) {
        return null;
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-slate-900">
                        {t('print_run_edit.title', { number: printRun.document_number })}
                    </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                    {/* Основная информация */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="book_id" className="text-sm font-medium text-slate-700">Книга</Label>
                            <Select value={formData.book_id || ''} onValueChange={(v) => handleFieldChange('book_id', v)}>
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue placeholder="Выберите книгу" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {(allBooks || []).map(b => (
                                        <SelectItem key={b.id} value={b.id} className="hover:bg-slate-100">
                                            {b.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="type" className="text-sm font-medium text-slate-700">{t('print_run_edit.type')}</Label>
                            <Select value={formData.type || ''} onValueChange={(v) => handleFieldChange('type', v)}>
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                                            <SelectItem value="main_print" className="hover:bg-slate-100">
                            {t('print_run_form.main_print')}
                        </SelectItem>
                        <SelectItem value="reprint" className="hover:bg-slate-100">
                            {t('print_run_form.reprint')}
                        </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="isbn" className="text-sm font-medium text-slate-700">ISBN</Label>
                        <Input
                            id="isbn"
                            value={formData.isbn || ''}
                            onChange={(e) => handleFieldChange('isbn', e.target.value)}
                            disabled={formData.type === 'reprint'}
                            className="bg-white border-slate-300"
                            placeholder="Введите ISBN"
                        />
                    </div>

                    {/* Производство и доставка */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="quantity" className="text-sm font-medium text-slate-700">Количество</Label>
                            <Input
                                id="quantity"
                                type="number"
                                min="1"
                                value={formData.quantity || ''}
                                onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value, 10) || 0)}
                                className="bg-white border-slate-300"
                                placeholder="Количество экземпляров"
                            />
                        </div>
                        <div>
                            <Label htmlFor="warehouse_id" className="text-sm font-medium text-slate-700">Склад назначения</Label>
                            <Select value={formData.warehouse_id || ''} onValueChange={(v) => handleFieldChange('warehouse_id', v)}>
                                <SelectTrigger className="bg-white border-slate-300">
                                    <SelectValue placeholder="Выберите склад" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {(allWarehouses || []).filter(w => w.is_active).map(w => (
                                        <SelectItem key={w.id} value={w.id} className="hover:bg-slate-100">
                                            {w.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label htmlFor="expected_delivery_date" className="text-sm font-medium text-slate-700">Ожидаемая дата поставки</Label>
                        <Input
                            id="expected_delivery_date"
                            type="date"
                            value={formData.expected_delivery_date || ''}
                            onChange={(e) => handleFieldChange('expected_delivery_date', e.target.value)}
                            className="bg-white border-slate-300"
                        />
                    </div>
                    
                    {/* Финансовая информация */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="cost_per_unit" className="text-sm font-medium text-slate-700">Себестоимость за экз. (₽)</Label>
                            <Input
                                id="cost_per_unit"
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.cost_per_unit || ''}
                                onChange={(e) => handleFieldChange('cost_per_unit', parseFloat(e.target.value) || 0)}
                                className="bg-white border-slate-300"
                                placeholder="0.00"
                            />
                        </div>
                        <div className="flex flex-col justify-end">
                            <Label className="text-sm font-medium text-slate-700">Общая стоимость</Label>
                            <p className="font-bold text-lg text-green-600 py-2 px-3 bg-green-50 rounded-lg mt-1">
                                {((formData.cost_per_unit || 0) * (formData.quantity || 0)).toLocaleString()} ₽
                            </p>
                        </div>
                    </div>

                    {/* Примечания */}
                    <div>
                        <Label htmlFor="notes" className="text-sm font-medium text-slate-700">Примечания</Label>
                        <Textarea
                            id="notes"
                            value={formData.notes || ''}
                            onChange={(e) => handleFieldChange('notes', e.target.value)}
                            className="bg-white border-slate-300 h-24"
                            placeholder={t('print_run_edit.notes_placeholder')}
                        />
                    </div>

                    <DialogFooter className="pt-6 gap-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                            <X className="w-4 h-4 mr-2" />
                            Отмена
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="bg-amber-600 hover:bg-amber-700">
                            <Save className="w-4 h-4 mr-2" />
                            {isSubmitting ? 'Сохранение...' : 'Сохранить'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}